import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpenCheck, RadioTower, Undo2, PlayCircle } from "lucide-react";
import { Layout } from "@/components/Layout";
import { WALK_STEPS, WalkthroughOverlay, applyWalkHighlights, clearWalkHighlights } from "@/standardsChange/Walkthrough";
import { TwoWayModel } from "@/standardsChange/TwoWayModel";
import { ActProposed } from "@/standardsChange/ActProposed";
import { ActImpact } from "@/standardsChange/ActImpact";
import { ActActivation } from "@/standardsChange/ActActivation";
import { ActRuntime } from "@/standardsChange/ActRuntime";
import { ReceiptsGallery } from "@/standardsChange/ReceiptsGallery";
import { SCENARIOS, VIEWS, CLOSING_CHAIN, DISCLAIMERS } from "@/standardsChange/data";
import { StatusChip } from "@/standardsChange/ui";

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "proposed", label: "Proposed Change" },
    { key: "impact", label: "Participant Impact" },
    { key: "activation", label: "Activation" },
    { key: "runtime", label: "Runtime" },
    { key: "receipts", label: "Receipts" },
];

const SCENARIO_TAB = {
    success: "runtime",
    invalid: "runtime",
    legacy: "impact",
    interpretation: "impact",
};

const VIEW_NOTES = {
    network: "Network view — all five independently operated nodes and the receipts moving between them.",
    steward: "Standards steward view — the steward sees published packages and bounded receipts only. It cannot inspect participants' private systems or approve an individual payment.",
    orig: "Originating institution view — its own local evidence, mandate and policy; other participants appear only as bounded receipt counterparties.",
    recv: "Receiving institution view — its own acceptance policy and controls; other participants appear only as bounded receipt counterparties.",
};

export default function StandardsChange() {
    const [tab, setTab] = useState("overview");
    const [scenario, setScenario] = useState("success");
    const [view, setView] = useState("network");
    const [runtimeMode, setRuntimeMode] = useState("success");
    const [walk, setWalk] = useState({ active: false, step: 0, paused: false, completed: false });
    const walkTimer = useRef(null);

    const startWalk = () => setWalk({ active: true, step: 0, paused: false, completed: false });
    const exitWalk = () => {
        clearWalkHighlights();
        setWalk({ active: false, step: 0, paused: false, completed: false });
    };
    const stepWalk = (dir) =>
        setWalk((w) => {
            const next = w.step + dir;
            if (next >= WALK_STEPS.length) return { ...w, completed: true };
            if (next < 0) return w;
            return { ...w, step: next };
        });

    useEffect(() => {
        if (!walk.active || walk.completed) {
            if (walk.completed) clearWalkHighlights();
            return;
        }
        const s = WALK_STEPS[walk.step];
        setTab(s.tab);
        if (s.scenario) setScenario(s.scenario);
        const t = setTimeout(() => applyWalkHighlights(s.targets), 350);
        return () => clearTimeout(t);
    }, [walk.active, walk.step, walk.completed]);

    useEffect(() => {
        if (!walk.active || walk.paused || walk.completed) return;
        walkTimer.current = setTimeout(() => stepWalk(1), 6000);
        return () => clearTimeout(walkTimer.current);
    }, [walk.active, walk.step, walk.paused, walk.completed]);

    const pickScenario = (key) => {
        setScenario(key);
        setTab(SCENARIO_TAB[key]);
        if (key === "success") setRuntimeMode("success");
        if (key === "invalid") setRuntimeMode("invalid");
    };

    return (
        <Layout>
            {/* Hero */}
            <section className="fade-up" data-testid="sc-hero">
                <div className="flex flex-wrap items-center gap-3">
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400">
                        Two-way standards-to-runtime demonstrator
                    </p>
                    <StatusChip tone="amber" testId="sc-mode-badge">SIMULATED</StatusChip>
                </div>
                <h1 className="mt-5 max-w-4xl text-4xl font-medium leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
                    From Standards to Governed Runtime
                </h1>
                <p className="mt-3 text-lg font-medium text-blue-950">
                    Publish once. Assess locally. Activate safely. Learn from runtime.
                </p>
                <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600">
                    Standards and scheme owners publish authoritative definitions and
                    usage rules. Each network participant retains its own evidence,
                    systems and execution authority. KATLAS enables their separately
                    hosted role algorithms to assess change, prove readiness, coordinate
                    activation and return structured operational feedback without
                    centralising the network.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                        onClick={startWalk}
                        data-testid="walkthrough-start-btn"
                        className="group inline-flex items-center gap-2 rounded-sm bg-blue-950 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                    >
                        <PlayCircle className="h-4 w-4" strokeWidth={1.5} />
                        Run 30-second walkthrough
                    </button>
                    <button
                        onClick={() => setTab("proposed")}
                        data-testid="sc-cta-run-journey"
                        className="group inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                    >
                        Run the change journey
                        <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </button>
                    <Link
                        to="/request/TX-BOND-001"
                        data-testid="sc-cta-runtime-demo"
                        className="inline-flex items-center rounded-sm border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                    >
                        View the runtime control demo
                    </Link>
                </div>

                {/* Three explanatory cards */}
                <div className="mt-10 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 md:grid-cols-3">
                    {[
                        [BookOpenCheck, "Publish", "The authorised steward publishes a signed, machine-readable change package."],
                        [RadioTower, "Assess and activate", "Each participant's local role algorithm assesses impact, tests the change and proves readiness."],
                        [Undo2, "Learn from runtime", "Exceptions and operational outcomes return bounded evidence to the appropriate steward."],
                    ].map(([Icon, title, body], i) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.06 * i, duration: 0.3 }}
                            data-testid={`sc-card-${i}`}
                            className="bg-white p-6"
                        >
                            <Icon className="h-5 w-5 text-blue-950" strokeWidth={1.5} />
                            <h3 className="mt-4 text-lg font-medium text-slate-900">{title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Controls */}
            <div className="sticky top-[57px] z-20 -mx-6 mt-10 border-y border-slate-200 bg-white/95 px-6 py-2.5 backdrop-blur" data-testid="sc-controls">
                <div className="flex flex-wrap items-center gap-1">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`sc-tab-${t.key}`}
                            className={`rounded-sm px-3 py-1.5 text-sm transition-colors duration-150 ${
                                tab === t.key
                                    ? "bg-blue-950 font-medium text-white"
                                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 pb-0.5 text-xs">
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="sc-scenario-controls">
                        <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">Scenario</span>
                        {SCENARIOS.map((s) => (
                            <button
                                key={s.key}
                                onClick={() => pickScenario(s.key)}
                                data-testid={`sc-scenario-${s.key}`}
                                className={`rounded-sm border px-2 py-1 transition-colors duration-150 ${
                                    scenario === s.key
                                        ? "border-blue-950 bg-blue-50 text-blue-950"
                                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="sc-view-controls">
                        <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">View</span>
                        {VIEWS.map((v) => (
                            <button
                                key={v.key}
                                onClick={() => setView(v.key)}
                                data-testid={`sc-view-${v.key}`}
                                className={`rounded-sm border px-2 py-1 transition-colors duration-150 ${
                                    view === v.key
                                        ? "border-blue-950 bg-blue-50 text-blue-950"
                                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                                }`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="sc-view-note">
                {VIEW_NOTES[view]}
            </p>

            {/* Sections */}
            <div className="mt-8 space-y-16">
                {tab === "overview" && (
                    <>
                        <TwoWayModel />
                        <Distinction />
                        <FinalScreen />
                    </>
                )}
                {tab === "proposed" && <ActProposed />}
                {tab === "impact" && <ActImpact view={view} scenario={scenario} />}
                {tab === "activation" && <ActActivation scenario={scenario} />}
                {tab === "runtime" && (
                    <ActRuntime mode={runtimeMode} onModeChange={setRuntimeMode} view={view} />
                )}
                {tab === "receipts" && <ReceiptsGallery />}
            </div>

            {/* Disclaimers */}
            <section className="mt-16 rounded-sm border border-slate-200 bg-white p-6" data-testid="sc-disclaimers">
                <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                    Disclaimers
                </p>
                <ul className="mt-3 grid gap-x-8 gap-y-1.5 text-xs leading-relaxed text-slate-500 sm:grid-cols-2">
                    {DISCLAIMERS.map((d) => <li key={d}>· {d}</li>)}
                </ul>
            </section>

            {walk.active && (
                <WalkthroughOverlay
                    step={walk.step}
                    paused={walk.paused}
                    completed={walk.completed}
                    onPrev={() => stepWalk(-1)}
                    onNext={() => stepWalk(1)}
                    onPauseToggle={() => setWalk((w) => ({ ...w, paused: !w.paused }))}
                    onExit={exitWalk}
                    onReplay={startWalk}
                />
            )}
        </Layout>
    );
}

function Distinction() {
    return (
        <section className="rounded-sm border border-slate-200 bg-white p-8" data-testid="sc-distinction">
            <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                Important distinction
            </p>
            <h2 className="mt-3 text-2xl font-medium tracking-tight text-slate-900">
                The source authority is governed centrally or sectorally. Execution
                authority remains distributed.
            </h2>
            <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 md:grid-cols-3">
                {[
                    ["The steward", "determines what has been published."],
                    ["Each participant", "determines what it is authorised and able to do locally."],
                    ["The network", "establishes whether those independently authorised actions remain compatible."],
                ].map(([who, what]) => (
                    <div key={who} className="bg-white p-5">
                        <p className="text-sm font-medium text-slate-900">{who}</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{what}</p>
                    </div>
                ))}
            </div>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-slate-600">
                KATLAS does not replace ISO, Swift, MyStandards, regulators, market
                infrastructures or participants' internal systems. It provides the
                distributed governance layer through which authoritative change
                packages are received, evaluated locally, activated in a coordinated
                way and answered with bounded runtime evidence.
            </p>
        </section>
    );
}

function FinalScreen() {
    return (
        <section className="rounded-sm border border-slate-200 bg-white p-8" data-testid="sc-final-screen">
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                One standard. Separate authorities. Governed network change.
            </h2>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                A common standard does not require one organisation to control every
                participant. The steward publishes the authoritative meaning and
                implementation conditions. Each participant evaluates and executes its
                own role locally. KATLAS connects publication, impact assessment,
                readiness, activation, runtime decisions and feedback through
                attributable receipts.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="sc-closing-chain">
                {CLOSING_CHAIN.map((s, i) => (
                    <span key={s} className="flex items-center gap-2">
                        <span className="rounded-sm bg-blue-950 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide text-white">
                            {s}
                        </span>
                        {i < CLOSING_CHAIN.length - 1 && <span className="text-slate-300">→</span>}
                    </span>
                ))}
            </div>
        </section>
    );
}
